import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { apiRequest } from "@/shared/utils/queryClient";
import { useToast } from "@/shared/hooks/use-toast";
import { bookAppointmentFormSchema } from "@shared/schema/browser";

const bookingFormSchema = bookAppointmentFormSchema.extend({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(1, "Phone number is required"),
  address: z.string().min(1, "Address is required"),
  postcode: z.string().min(1, "Postcode is required"),
  serviceType: z.enum(["inspection", "treatment", "emergency"]),
  appointmentDate: z.date(),
});

type BookingFormData = z.infer<typeof bookingFormSchema>;

interface BookingSystemProps {
  onClose: () => void;
}

export default function BookingSystem({ onClose }: BookingSystemProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const { toast } = useToast();

  const form = useForm<BookingFormData>({
    resolver: zodResolver(bookingFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      postcode: "",
      serviceType: "inspection",
      description: "",
    },
  });

  const bookingMutation = useMutation({
    mutationFn: (data: BookingFormData) =>
      apiRequest("POST", "/api/appointments", data),
    onSuccess: () => {
      toast({
        title: "Appointment booked successfully!",
        description: "We will send you a confirmation email shortly.",
      });
      onClose();
    },
    onError: () => {
      toast({
        title: "Error booking appointment",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    },
  });

  const availableTimes = ["9:00 AM", "11:00 AM", "2:00 PM", "4:00 PM"];

  // Generate next 14 days for calendar
  const generateCalendarDays = () => {
    const days = [];
    const today = new Date();
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const calendarDays = generateCalendarDays();

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    const dateTime = new Date(date);
    if (selectedTime) {
      const [time, period] = selectedTime.split(" ");
      const [hours, minutes] = time.split(":").map(Number);
      let hour24 = hours;
      if (period === "PM" && hours !== 12) hour24 += 12;
      if (period === "AM" && hours === 12) hour24 = 0;

      dateTime.setHours(hour24, minutes, 0, 0);
      form.setValue("appointmentDate", dateTime);
    }
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    if (selectedDate) {
      const dateTime = new Date(selectedDate);
      const [timeStr, period] = time.split(" ");
      const [hours, minutes] = timeStr.split(":").map(Number);
      let hour24 = hours;
      if (period === "PM" && hours !== 12) hour24 += 12;
      if (period === "AM" && hours === 12) hour24 = 0;

      dateTime.setHours(hour24, minutes, 0, 0);
      form.setValue("appointmentDate", dateTime);
    }
  };

  const onSubmit = (data: BookingFormData) => {
    if (!selectedDate || !selectedTime) {
      toast({
        title: "Please select date and time",
        description: "Both date and time are required for booking.",
        variant: "destructive",
      });
      return;
    }
    bookingMutation.mutate(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Book an Appointment
          </h2>
          <button onClick={onClose}>
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">
              Select Date & Time
            </h3>

            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <div className="grid grid-cols-7 gap-1 text-center text-sm mb-2">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                  (day) => (
                    <div key={day} className="font-semibold">
                      {day}
                    </div>
                  ),
                )}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.slice(0, 14).map((day, index) => (
                  <button
                    key={index}
                    onClick={() => handleDateSelect(day)}
                    className={`p-2 rounded text-sm transition-colors ${
                      selectedDate?.toDateString() === day.toDateString()
                        ? "bg-emerald-600 text-white"
                        : "hover:bg-emerald-50 hover:text-emerald-600"
                    }`}
                  >
                    {day.getDate()}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Available Times</h4>
              <div className="grid grid-cols-2 gap-2">
                {availableTimes.map((time) => (
                  <button
                    key={time}
                    onClick={() => handleTimeSelect(time)}
                    className={`p-2 border rounded text-sm transition-colors ${
                      selectedTime === time
                        ? "border-emerald-500 bg-emerald-50"
                        : "border-gray-300 hover:border-emerald-500 hover:bg-emerald-50"
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 mb-4">
              Service Details
            </h3>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Type
                </label>
                <select
                  {...form.register("serviceType")}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3"
                >
                  <option value="inspection">Inspection</option>
                  <option value="treatment">Treatment Service</option>
                  <option value="emergency">Emergency Service</option>
                  <option value="quarterly">Quarterly Service Plan</option>
                  <option value="monthly">Monthly Service Plan</option>
                  <option value="one-time">One-Time Treatment</option>
                  <option value="rodent-control">Rodent Control</option>
                  <option value="bird-proofing">Bird Proofing</option>
                  <option value="pest-disinfection">Pest Disinfection</option>
                  <option value="fumigation">Fumigation Treatment</option>
                  <option value="heat-treatment">Heat Treatment</option>
                  <option value="drain-survey">Drain Survey & Report</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <input
                  {...form.register("name")}
                  type="text"
                  placeholder="Full Name"
                  className="border border-gray-300 rounded-lg px-4 py-3"
                />
                <input
                  {...form.register("email")}
                  type="email"
                  placeholder="Email"
                  className="border border-gray-300 rounded-lg px-4 py-3"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <input
                  {...form.register("phone")}
                  type="tel"
                  placeholder="Phone Number"
                  className="border border-gray-300 rounded-lg px-4 py-3"
                />
                <input
                  {...form.register("postcode")}
                  type="text"
                  placeholder="Postcode"
                  className="border border-gray-300 rounded-lg px-4 py-3"
                />
              </div>

              <input
                {...form.register("address")}
                type="text"
                placeholder="Full Address"
                className="w-full border border-gray-300 rounded-lg px-4 py-3"
              />

              <textarea
                {...form.register("description")}
                rows={3}
                placeholder="Describe your pest problem or special requirements..."
                className="w-full border border-gray-300 rounded-lg px-4 py-3"
              />

              <div className="bg-emerald-50 p-4 rounded-lg">
                <h4 className="font-semibold text-emerald-900 mb-2">
                  Booking Summary
                </h4>
                <div className="text-sm space-y-1">
                  <div>
                    Date:{" "}
                    <span className="font-semibold">
                      {selectedDate?.toLocaleDateString("en-GB", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      }) || "Not selected"}
                    </span>
                  </div>
                  <div>
                    Time:{" "}
                    <span className="font-semibold">
                      {selectedTime || "Not selected"}
                    </span>
                  </div>
                  <div>
                    Service:{" "}
                    <span className="font-semibold">
                      {form.watch("serviceType") === "inspection"
                        ? "Inspection"
                        : form.watch("serviceType") === "treatment"
                          ? "Treatment Service"
                          : form.watch("serviceType") === "emergency"
                            ? "Emergency Service"
                            : form.watch("serviceType") === "quarterly"
                              ? "Quarterly Service Plan"
                              : form.watch("serviceType") === "monthly"
                                ? "Monthly Service Plan"
                                : form.watch("serviceType") === "one-time"
                                  ? "One-Time Treatment"
                                  : form.watch("serviceType") ===
                                      "rodent-control"
                                    ? "Rodent Control"
                                    : form.watch("serviceType") ===
                                        "bird-proofing"
                                      ? "Bird Proofing"
                                      : form.watch("serviceType") ===
                                          "pest-disinfection"
                                        ? "Pest Disinfection"
                                        : form.watch("serviceType") ===
                                            "fumigation"
                                          ? "Fumigation Treatment"
                                          : form.watch("serviceType") ===
                                              "heat-treatment"
                                            ? "Heat Treatment"
                                            : form.watch("serviceType") ===
                                                "drain-survey"
                                              ? "Drain Survey & Report"
                                              : form.watch("serviceType")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={bookingMutation.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {bookingMutation.isPending ? "Booking..." : "Confirm Booking"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
